import { toast } from 'react-toastify'
import { successToast } from '../../../utils/toastService'
import { StreamController, StreamEvent, LoaderControls } from './types'

// Standardized messages
export const UPLOAD_MESSAGES = {
  preparing: 'Uploading file. Preparing to process samples…',
  started: 'Upload started. Processing samples, please wait…',
  reconnected: 'Reconnected. Processing samples, please wait…',
  processing: 'Processing samples',
  defaultProgress: 'Uploading file...',
  defaultStart: 'Starting upload...',
  defaultSuccess: 'File uploaded successfully',
  defaultComplete: 'Upload completed',
  defaultFailed: 'File upload failed',
} as const

// Event type checks
const COMPLETION_TYPES = ['done', 'completed', 'success']
const ERROR_TYPES = ['failed', 'error']
const START_TYPES = ['accepted', 'start']

export const isCompletionEvent = (type?: string) =>
  type && COMPLETION_TYPES.includes(type.toLowerCase())

export const isErrorEvent = (type?: string) =>
  type && ERROR_TYPES.includes(type.toLowerCase())

export const isStartEvent = (type?: string) =>
  type && START_TYPES.includes(type.toLowerCase())

export const isProgressEvent = (type?: string) =>
  type?.toLowerCase() === 'progress'

// Show appropriate toast based on completion status
export const showCompletionToast = (
  message: string,
  isPartialSuccess: boolean
) => {
  if (isPartialSuccess) {
    toast.info(message)
  } else {
    successToast(message)
  }
}

// Safely close a stream controller
export const closeController = (controller: StreamController | null) => {
  try {
    controller?.close()
  } catch (_) {}
}

// Extract message from event
export const getEventMessage = (evt: StreamEvent) => evt?.message || evt?.detail

/**
 * Handle stream events with standardized progress/completion logic
 */
export const handleStreamProgress = (
  evt: StreamEvent,
  loaderControls: LoaderControls,
  loaderId?: string
) => {
  const type = evt?.type?.toLowerCase()
  const message = getEventMessage(evt)
  const { total, imported } = evt

  if (isProgressEvent(type) && total != null && imported != null) {
    loaderControls.setLoaderText(
      message || UPLOAD_MESSAGES.defaultProgress,
      loaderId
    )
    loaderControls.setProgress(
      imported,
      total,
      UPLOAD_MESSAGES.processing,
      loaderId
    )
  } else if (isStartEvent(type)) {
    loaderControls.setLoaderText(
      message || UPLOAD_MESSAGES.defaultStart,
      loaderId
    )
    if (total != null) {
      loaderControls.setProgress(0, total, UPLOAD_MESSAGES.processing, loaderId)
    }
  }
}

/**
 * Process stream event and call appropriate callback
 */
export type StreamEventCallbacks = {
  onComplete: (evt: StreamEvent, isPartialSuccess: boolean) => void
  onError: (evt: StreamEvent) => void
}

export const processStreamEvent = (
  evt: StreamEvent,
  loaderControls: LoaderControls,
  callbacks: StreamEventCallbacks,
  loaderId?: string
) => {
  const type = evt?.type?.toLowerCase()

  // Always handle progress updates
  handleStreamProgress(evt, loaderControls, loaderId)

  // Check for terminal events
  if (isCompletionEvent(type)) {
    const isPartialSuccess = evt?.status === false
    callbacks.onComplete(evt, isPartialSuccess)
  } else if (isErrorEvent(type)) {
    callbacks.onError(evt)
  }
}
